import { css } from '@styled-system/css';
import { FolderPlus, Search, Star } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAnalysisMenuListData } from '@/features/analysisMenu/hooks/useAnalysisMenuListData';
import type { AnalysisItemData } from '@/features/analysisMenu/types';
import { FolderFormModal } from '@/features/productSearch/components/FolderFormModal';
import { RenameSearchModal } from '@/features/productSearch/components/RenameSearchModal';
import { SearchHistoryFolders } from '@/features/productSearch/components/SearchHistoryFolders';
import {
  useSearchOrganizationServer,
  type SearchFolder,
} from '@/features/productSearch/organization/useSearchOrganizationServer';
import { AnalysisEngineBanner } from '@/shared/components/analysis/AnalysisEngineBanner';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Button } from '@/shared/components/ui/Button';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { Spinner } from '@/shared/components/ui/Spinner';
import { QuotaReachedModal } from '@/features/credits/QuotaReachedModal';
import {
  QUOTA_ERROR_MESSAGE,
  useCreateAnalysis,
  useCreateVestiaireAnalysis,
  useRenameAnalysis,
} from '@/shared/hooks/analysis/mutations';

import { MOCKS_ENABLED } from '@/mocks/graphqlMockHandlers';

import { VendorSearchBar, type VendorSearchBarInput } from './VendorSearchBar';

// ============================================================================
// Page Recherche vendeur — même fonctionnement que la recherche produit :
// hero spotlight + historique organisé en dossiers (store d'organisation
// vendeur, indépendant de celui des produits).
// ============================================================================

const page = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '10',
  width: '100%',
  paddingTop: { base: '4', md: '8' },
});

const heroWrap = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '7',
  paddingY: { base: '6', md: '12' },
  marginBottom: { base: '4', md: '10' },
});

const heroTitle = css({
  fontSize: 'h1',
  lineHeight: 'h1',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.8px',
});

const heroHint = css({
  fontSize: 'caption',
  color: 'text.tertiary',
});

const historySection = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
});

const historyHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const historyTitle = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: 'medium',
});

const emptyState = css({
  paddingY: '16',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3',
  color: 'text.tertiary',
  fontSize: 'bodySm',
  textAlign: 'center',
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  paddingY: '16',
});

// ============================================================================

export const VendorSearchPage: FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isBarOpen, setIsBarOpen] = useState(false);
  const [createInFolderId, setCreateInFolderId] = useState<string | null>(null);
  const [folderModalMode, setFolderModalMode] = useState<'create' | 'rename' | null>(null);
  const [folderToEdit, setFolderToEdit] = useState<SearchFolder | null>(null);
  const [filterText, setFilterText] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [renameTarget, setRenameTarget] = useState<AnalysisItemData | null>(null);
  const { renameAnalysis } = useRenameAnalysis();

  const { analyses, loading, error } = useAnalysisMenuListData('vendor');
  const { createAnalysis } = useCreateAnalysis();
  const { createVestiaireAnalysis } = useCreateVestiaireAnalysis();
  const { folders, actions } = useSearchOrganizationServer('profile');

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');
  };

  const buildMeta = (analysis: AnalysisItemData): string => {
    const metaParts: string[] = [];
    if (analysis.createdAt) metaParts.push(t('productSearch.createdOn', { date: formatDate(analysis.createdAt) }));
    // Création asynchrone : le compteur n'existe qu'une fois le calcul fini
    if (analysis.computeState === 'queued' || analysis.computeState === 'running') {
      metaParts.push(t('productSearch.computing'));
    } else if (analysis.computeState === 'failed') {
      metaParts.push(t('productSearch.computeFailed'));
    } else if (analysis.analysedProductCount) {
      metaParts.push(t('productSearch.productsAnalyzed', { count: analysis.analysedProductCount }));
    }
    return metaParts.join(' · ');
  };

  const [quotaModalOpen, setQuotaModalOpen] = useState(false);

  const handleSubmit = async (input: VendorSearchBarInput) => {
    const isVestiaire = input.platform === 'vestiaire';
    // Jamais de repli Vinted pour une entrée VC : sans id vendeur résolu, on s'arrête là — même
    // message générique de création que Vinted (productSearch.createError).
    if (isVestiaire && !input.sellerId) throw new Error(t('productSearch.createError'));
    const result =
      isVestiaire && input.sellerId
        ? await createVestiaireAnalysis({ title: input.title, seller: input.sellerId })
        : await createAnalysis({ title: input.title, url: input.url, type: 'vendor' });
    if (result.success && result.analysis) {
      // Dossier choisi + épinglage EN TÊTE de sa scope (le back place les
      // nouvelles recherches en fin de dossier par défaut)
      void actions.moveSearch(result.analysis.id, input.folderId ?? null, 0);
      navigate(`/analysis/vendors/${result.analysis.id}`);
    } else if (result.error === QUOTA_ERROR_MESSAGE) {
      // Quota mensuel atteint : popup avec CTA abonnement
      setQuotaModalOpen(true);
    } else if (isVestiaire && result.error) {
      // VC : le message serveur est parlant (ex. 409 « analyse déjà en cours »)
      throw new Error(result.error);
    } else {
      throw new Error(t('productSearch.createError'));
    }
  };

  const handleFolderSubmit = (name: string) => {
    if (folderModalMode === 'rename' && folderToEdit) {
      void actions.renameFolder(folderToEdit.id, name);
    } else {
      void actions.createFolder(name);
    }
  };

  return (
    <AppLayout
      title={t('vendorSearch.title')}
      headerStats={
        <>
          {!loading && analyses.length > 0 && (
            <span>{t('vendorSearch.totalSearches', { count: analyses.length })}</span>
          )}
        </>
      }
    >
      <div className={page}>
        {/* État du moteur d'analyses — rend `null` quand tout va bien. */}
        <AnalysisEngineBanner />

        {/* ─── Hero — barre « expanding » : le panneau de critères se
            déplie sous la barre, pas d'overlay ───────────────────────── */}
        <div className={heroWrap}>
          <h1 className={heroTitle}>{t('vendorSearch.heroTitle')}</h1>
          <VendorSearchBar
            isOpen={isBarOpen}
            onOpenChange={(open) => {
              setIsBarOpen(open);
              if (!open) setCreateInFolderId(null);
            }}
            onSubmit={handleSubmit}
            initialFolderId={createInFolderId}
            folders={folders}
          />
          <span className={heroHint}>{t('vendorSearch.heroHint')}</span>
        </div>

        {/* ─── History organisé en dossiers ────────────────────────────── */}
        <div className={historySection}>
          <div className={historyHeader}>
            <span className={historyTitle}>
              {t('productSearch.history')}
              {MOCKS_ENABLED && (
                <span className={css({ marginLeft: '3', color: 'warning.default', textTransform: 'none' })}>
                  {t('productSearch.demo')}
                </span>
              )}
            </span>
          </div>

          {/* Toolbar : recherche + filtre favoris + nouveau dossier */}
          <div className={css({ display: 'flex', alignItems: 'center', gap: '3', flexWrap: 'wrap' })}>
            <SearchBarNew placeholder={t('folders.filterPlaceholder')} value={filterText} onChange={setFilterText} />
            <ChipToggle selected={favoritesOnly} onChange={setFavoritesOnly}>
              <span className={css({ display: 'inline-flex', alignItems: 'center', gap: '2' })}>
                <Star size={12} strokeWidth={1.75} fill={favoritesOnly ? 'currentColor' : 'none'} />
                {t('folders.favorites')}
              </span>
            </ChipToggle>
            <Button
              variant="secondary"
              size="md"
              icon={<FolderPlus size={13} strokeWidth={1.75} />}
              className={css({ ml: 'auto' })}
              onClick={() => {
                setFolderToEdit(null);
                setFolderModalMode('create');
              }}
            >
              {t('folders.newFolder')}
            </Button>
          </div>

          {loading && (
            <div className={centered}>
              <Spinner size={22} />
            </div>
          )}

          {!loading && error && <div className={emptyState}>{error}</div>}

          {!loading && !error && analyses.length === 0 && (
            <div className={emptyState}>
              <Search size={20} strokeWidth={1.5} />
              <span>
                {t('vendorSearch.emptyTitle')}
                <br />
                {t('vendorSearch.emptySubtitle')}
              </span>
            </div>
          )}

          {!loading && analyses.length > 0 && (
            <SearchHistoryFolders
              analyses={analyses}
              buildMeta={buildMeta}
              folders={folders}
              actions={actions}
              filterText={filterText}
              favoritesOnly={favoritesOnly}
              onOpen={(id) => navigate(`/analysis/vendors/${id}`)}
              onRenameFolder={(folder) => {
                setFolderToEdit(folder);
                setFolderModalMode('rename');
              }}
              onCreateSearch={(folderId) => {
                setCreateInFolderId(folderId);
                setIsBarOpen(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onRenameSearch={(analysis) => setRenameTarget(analysis)}
            />
          )}
        </div>
      </div>

      {/* Renommage rapide (menu ⋯) */}
      <RenameSearchModal
        isOpen={renameTarget !== null}
        initialName={renameTarget?.name ?? ''}
        onClose={() => setRenameTarget(null)}
        onSubmit={async (name) => {
          if (renameTarget) await renameAnalysis(renameTarget.id, name);
        }}
      />

      <FolderFormModal
        isOpen={folderModalMode !== null}
        onClose={() => {
          setFolderModalMode(null);
          setFolderToEdit(null);
        }}
        onSubmit={handleFolderSubmit}
        mode={folderModalMode ?? 'create'}
        initialName={folderToEdit?.name}
      />

      <QuotaReachedModal isOpen={quotaModalOpen} kind="vendor" onClose={() => setQuotaModalOpen(false)} />
    </AppLayout>
  );
};
